import { useTranslation } from "react-i18next";
import {
  Box,
  Heading,
  Text,
  Container,
  Stack,
  SimpleGrid,
  Flex,
  Icon,
  Button,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const colors = {
    teal: "#1da179",
    tealLight: "#34c49b",
    grayBg: "#f9f9f9",
    dark: "#0f172a",
  };

  // Motion wrappers
  const MotionBox = motion(Box);
  const MotionHeading = motion(Heading);
  const MotionText = motion(Text);
  const MotionFlex = motion(Flex);
  const MotionButton = motion(Button);

  return (
    <Box bg="white">
      {/* === HERO INTRO === */}
      <Box
        bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
        color="white"
        py={{ base: 20, md: 28 }}
        textAlign="center"
        position="relative"
        overflow="hidden"
      >
        {/* Decorative background */}
        <Box
          position="absolute"
          top="-20"
          left="-20"
          w="60"
          h="60"
          bg="whiteAlpha.300"
          borderRadius="full"
          filter="blur(70px)"
        />
        <Container maxW="6xl" position="relative" zIndex="1">
          <Stack spacing={6}>
            <MotionHeading
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="extrabold"
              initial={{ opacity: 0, y: -30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {t("about.title")}
            </MotionHeading>
            
            <MotionText
              fontSize={{ base: "md", md: "lg" }}
              maxW="4xl"
              mx="auto"
              opacity={0.9}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
            >
              {t("about.description")}
            </MotionText>
          </Stack>
        </Container>
      </Box>

      {/* === VISION & MISSION === */}
      <Box py={20} bg={colors.grayBg}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
            {/* Vision */}
            <MotionBox
              p={10}
              borderRadius="2xl"
              bg="whiteAlpha.800"
              backdropFilter="blur(12px)"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="xl"
              textAlign={isArabic ? "right" : "left"}
              initial={{ opacity: 0, x: isArabic ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              whileHover={{ y: -6 }}
            >
  <Heading
  size="lg"
  lineHeight="1.6"
  bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
  bgClip="text"
  mb={4}
>
  {isArabic ? "الرؤية" : "Vision"}
</Heading>
              <Text fontSize="md" color="gray.700" lineHeight="taller">
                {t("about.vision")}
              </Text>
            </MotionBox>

            {/* Mission */}
            <MotionBox
              p={10}
              borderRadius="2xl"
              bg="whiteAlpha.800"
              backdropFilter="blur(12px)"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="xl"
              textAlign={isArabic ? "right" : "left"}
              initial={{ opacity: 0, x: isArabic ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ y: -6 }}
            >
              <Heading
  size="lg"
  lineHeight="1.6"
  bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
  bgClip="text"
  mb={4}
              >
                {isArabic ? "الرسالة" : "Mission"}
              </Heading>
              <Text fontSize="md" color="gray.700" lineHeight="taller">
                {t("about.mission")}
              </Text>
            </MotionBox>
          </SimpleGrid>
        </Container>
      </Box>

      {/* === WHY ACT POLYMERS === */}
      <Box py={20} bg="white">
        <Container maxW="6xl">
        <MotionHeading
  textAlign="center"
  size="xl"
  mb={12}
  fontWeight="extrabold"
>
  <Box
    as="span"
    bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
    bgClip="text"
    lineHeight="1.4"
    pb={2}
    display="inline-block"
  >
    {t("about.why.title")}
  </Box>
</MotionHeading>


          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} maxW="5xl" mx="auto">
            {t("about.why.points", { returnObjects: true }).map((point, idx) => (
              <MotionFlex
                key={idx}
                p={6}
                bg={colors.grayBg}
                borderRadius="lg"
                align="center"
                border="1px solid"
                borderColor="gray.200"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                whileHover={{ scale: 1.02 }}
              >
                <Icon as={CheckCircleIcon} color={colors.teal} boxSize={6} mr={3} />
                <Text fontSize="md" fontWeight="medium" color="gray.800">
                  {point}
                </Text>
              </MotionFlex>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* === CONTACT CTA === */}
      <Box
        py={20}
        px={6}
        bgGradient={`linear(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`}
        color="white"
        textAlign="center"
        position="relative"
        overflow="hidden"
      >
        {/* Decorative shapes */}
        <Box
          position="absolute"
          bottom="-20"
          right="-20"
          w="72"
          h="72"
          bg="whiteAlpha.300"
          borderRadius="full"
          filter="blur(100px)"
        />
        <Container maxW="5xl" position="relative" zIndex="1">
          <MotionHeading
            size="lg"
            mb={4}
            fontWeight="extrabold"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            {isArabic ? "تواصل معنا اليوم" : "Get in Touch Today"}
          </MotionHeading>
          <MotionText
            mb={8}
            maxW="3xl"
            mx="auto"
            fontSize="lg"
            opacity={0.9}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
          >
            {isArabic
              ? "دعنا نساعدك في رحلتك مع حلول البوليمرات المبتكرة"
              : "Let’s help you achieve more with our innovative polymer solutions."}
          </MotionText>
          <MotionButton
            as="a"
            href="/contact"
            size="lg"
            px={10}
            py={6}
            fontWeight="bold"
            color={colors.teal}
            bg="white"
            borderRadius="full"
            boxShadow="lg"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            _hover={{ bg: "gray.100" }}
          >
            {isArabic ? "اتصل بنا" : "Contact Us"}
          </MotionButton>
        </Container>
      </Box>
    </Box>
  );
}
